import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  CircularProgress,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import { OrderCategory, OrderSubcategory } from '../../types/order';

const HARDWARE_TYPES = [
  { value: 'router', label: 'Router' },
  { value: 'ata', label: 'ATA' },
  { value: 'other', label: 'Other' },
];

function HardwarePage() {
  const [hardware, setHardware] = useState<any[]>([]);
  const [categories, setCategories] = useState<OrderCategory[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<OrderSubcategory[]>([]);
  const [subcategories, setSubcategories] = useState<OrderSubcategory[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHardware, setEditingHardware] = useState<any | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    hardware_type: 'other' as string,
    category_id: '',
    name: '',
    description: '',
    price: '',
    delivery_fee: '0',
    discounted_price: '',
    discount_start_at: '',
    discount_end_at: '',
    price_no_contract: '',
    price_12_month: '',
    price_24_month: '',
    subcategory_ids: [] as string[],
    display_order: 0,
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      loadSubcategories(formData.category_id);
    } else {
      setSubcategories([]);
      setFormData((p) => ({ ...p, subcategory_ids: [] }));
    }
  }, [formData.category_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [hwData, catData, subcatData] = await Promise.all([
        api.adminListHardware(),
        api.adminListCategories(),
        api.adminListSubcategories(),
      ]);
      setHardware(hwData);
      setCategories(catData);
      setAllSubcategories(subcatData);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    try {
      const subcats = await api.adminListSubcategories(categoryId);
      setSubcategories(subcats);
    } catch (err) {
      setSubcategories([]);
    }
  };

  const handleOpenDialog = (item?: any) => {
    if (item) {
      setEditingHardware(item);
      setFormData({
        hardware_type: item.hardware_type || 'other',
        category_id: item.category_id || '',
        name: item.name,
        description: item.description || '',
        price: String(item.price ?? ''),
        delivery_fee: String(item.delivery_fee ?? '0'),
        discounted_price: item.discounted_price != null ? String(item.discounted_price) : '',
        discount_start_at: item.discount_start_at ? item.discount_start_at.slice(0, 16) : '',
        discount_end_at: item.discount_end_at ? item.discount_end_at.slice(0, 16) : '',
        price_no_contract: item.price_no_contract != null ? String(item.price_no_contract) : '',
        price_12_month: item.price_12_month != null ? String(item.price_12_month) : '',
        price_24_month: item.price_24_month != null ? String(item.price_24_month) : '',
        subcategory_ids: item.subcategory_ids || [],
        display_order: item.display_order ?? 0,
        is_active: item.is_active ?? true,
      });
      if (item.category_id) loadSubcategories(item.category_id);
    } else {
      setEditingHardware(null);
      setFormData({
        hardware_type: 'other',
        category_id: '',
        name: '',
        description: '',
        price: '',
        delivery_fee: '0',
        discounted_price: '',
        discount_start_at: '',
        discount_end_at: '',
        price_no_contract: '',
        price_12_month: '',
        price_24_month: '',
        subcategory_ids: [],
        display_order: 0,
        is_active: true,
      });
      setSubcategories([]);
    }
    setImageFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingHardware(null);
    setImageFile(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const payload: any = {
        hardware_type: formData.hardware_type,
        category_id: formData.category_id,
        name: formData.name,
        description: formData.description || undefined,
        price: parseFloat(formData.price) || 0,
        delivery_fee: parseFloat(formData.delivery_fee) || 0,
        discounted_price: formData.discounted_price ? parseFloat(formData.discounted_price) : undefined,
        discount_start_at: formData.discount_start_at || undefined,
        discount_end_at: formData.discount_end_at || undefined,
        price_no_contract: formData.price_no_contract ? parseFloat(formData.price_no_contract) : undefined,
        price_12_month: formData.price_12_month ? parseFloat(formData.price_12_month) : undefined,
        price_24_month: formData.price_24_month ? parseFloat(formData.price_24_month) : undefined,
        subcategory_ids: formData.subcategory_ids,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };
      let id: string;
      if (editingHardware) {
        await api.adminUpdateHardware(editingHardware.id, payload);
        id = editingHardware.id;
      } else {
        const created = await api.adminCreateHardware(payload);
        id = created.id;
      }
      if (imageFile) {
        await api.adminUploadHardwareImage(id, imageFile);
      }
      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this hardware item?')) return;
    try {
      await api.adminDeleteHardware(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const formatPrice = (v?: number) => (v != null ? `$${Number(v).toFixed(2)}` : '—');
  const getCategoryName = (cid?: string) => categories.find((c) => c.id === cid)?.name ?? '—';
  const getSubcategoryNames = (ids: string[]) =>
    ids.map((id) => allSubcategories.find((s) => s.id === id)?.name).filter(Boolean).join(', ') || '—';

  const filtered = filterCategoryId ? hardware.filter((h) => h.category_id === filterCategoryId) : hardware;

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'hardware_type', headerName: 'Type', width: 100 },
    { field: 'category_name', headerName: 'Category', width: 140, valueGetter: (p: any) => getCategoryName(p.row?.category_id) },
    {
      field: 'subcategories',
      headerName: 'Assigned to',
      width: 200,
      valueGetter: (p: any) => getSubcategoryNames(p.row?.subcategory_ids || []),
    },
    { field: 'price', headerName: 'Price', width: 90, renderCell: (p) => formatPrice(p.value) },
    { field: 'delivery_fee', headerName: 'Delivery', width: 90, renderCell: (p) => formatPrice(p.value) },
    { field: 'discounted_price', headerName: 'Promo Price', width: 100, renderCell: (p) => formatPrice(p.value) },
    { field: 'display_order', headerName: 'Order', width: 80 },
    { field: 'is_active', headerName: 'Active', width: 80, type: 'boolean' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (p: any) => (
        <Box>
          <IconButton size="small" onClick={() => handleOpenDialog(p.row)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(p.row.id)} color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Hardware</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Hardware
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={filterCategoryId}
              label="Filter by Category"
              onChange={(e) => setFilterCategoryId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filtered}
            columns={columns}
            paginationModel={{ page: 0, pageSize: 25 }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            getRowId={(r) => r.id}
          />
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingHardware ? 'Edit Hardware' : 'Add Hardware'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Hardware Type</InputLabel>
                <Select
                  value={formData.hardware_type}
                  label="Hardware Type"
                  onChange={(e) => setFormData({ ...formData, hardware_type: e.target.value })}
                >
                  {HARDWARE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Description (rich text)</Typography>
              <Box sx={{ minHeight: 120, '& .quill': { bgcolor: 'background.paper' } }}>
                <ReactQuill
                  theme="snow"
                  value={formData.description}
                  onChange={(v) => setFormData({ ...formData, description: v })}
                  style={{ minHeight: 120 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Price ($)"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Delivery Fee ($)"
                value={formData.delivery_fee}
                onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Discounted Price ($)"
                value={formData.discounted_price}
                onChange={(e) => setFormData({ ...formData, discounted_price: e.target.value })}
                helperText="Promo price"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Discount Start"
                value={formData.discount_start_at}
                onChange={(e) => setFormData({ ...formData, discount_start_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Discount End"
                value={formData.discount_end_at}
                onChange={(e) => setFormData({ ...formData, discount_end_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Contract pricing (optional — leave blank to use standard price)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="No Contract Price ($)"
                value={formData.price_no_contract}
                onChange={(e) => setFormData({ ...formData, price_no_contract: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="12 Month Contract Price ($)"
                value={formData.price_12_month}
                onChange={(e) => setFormData({ ...formData, price_12_month: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="24 Month Contract Price ($)"
                value={formData.price_24_month}
                onChange={(e) => setFormData({ ...formData, price_24_month: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned to (Subcategories)</InputLabel>
                <Select
                  multiple
                  value={formData.subcategory_ids}
                  label="Assigned to (Subcategories)"
                  onChange={(e) => setFormData({ ...formData, subcategory_ids: e.target.value as string[] })}
                  renderValue={(sel) =>
                    (sel as string[])
                      .map((id) => subcategories.find((s) => s.id === id)?.name)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {subcategories.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={formData.subcategory_ids.includes(s.id)} />
                      <ListItemText primary={s.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                e.g. NBN and Supa for Internet
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Display Order"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<ImageIcon />} fullWidth>
                {imageFile ? imageFile.name : editingHardware?.image_path ? 'Change image' : 'Upload image'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || !formData.category_id || !formData.price}
          >
            {editingHardware ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HardwarePage;
